import React, { useState, useEffect, useCallback } from 'react'
import { Form, Row, Col, Spinner, Alert } from 'react-bootstrap'
import MainData from '../Form/FormParts/MainData'
import JobFunctions from '../Form/FormParts/JobFunctions'
import { useAssetDataState } from '../../../stores/AssetDataProvider'
import { useAssetTypesState } from '../../../stores/Configuration/AssetTypesProvider'
import { useTranslation } from 'react-i18next'
import useMountEffect from '../../../utils/hooks/useMountEffect'
import { emptyGuid, isEmptyGuid } from '../../../utils/emptyGuid'
import styled from 'styled-components'
import AssetGroupSelect from '../../Employees/Form/FormComponents/AssetGroupSelect'
import ArraySelect from '../../Select/ArraySelect'
import apiClient from '../../../utils/api/apiClient'
import { APIEndpoints } from '../../../utils/api/apiConfig'

const StyledWrapper = styled.div`
  .form-label {
    white-space: nowrap;
  }
`

const AddContentArea = () => {
  const {
    formErrors,
    setFormErrors,
    mainData,
    setMainDataByKey,
    resetErrorByFormKey,
    activities,
    setActivitiesByKey,
    selectableAvtivities,
    loadingActivities,
  } = useAssetDataState()
  const { assetTypes, getAssetTypes } = useAssetTypesState()
  const [filteredAssetTypes, setFilteredAssetTypes] = useState([])
  const { t } = useTranslation()
  const [fieldConfiguration, setFieldConfiguration] = useState(null)
  const [selectedTypeId, setSelectedTypeId] = useState(emptyGuid)
  const [selectedGroupId, setSelectedGroupId] = useState(emptyGuid)
  const [isGrouped, setIsGrouped] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  useMountEffect(() => {
    getAssetTypes()
  })
  useEffect(() => {
    let cleared = assetTypes.filter(
      (item) => !item.isSystemType && !item.isGrouped
    )
    if (cleared.length === 1) {
      setSelectedTypeId(cleared[0].id)
    }
    setFilteredAssetTypes(cleared)
  }, [assetTypes])
  const initConfiguration = useCallback(
    (selectedTypeId) => {
      let found = assetTypes.find((item) => item.id === selectedTypeId)
      if (found) {
        setFieldConfiguration(found.fieldConfiguration)
        setMainDataByKey('type.id', selectedTypeId)
      } else {
        setFieldConfiguration(null)
        setMainDataByKey('type.id', selectedTypeId)
      }
    },
    [assetTypes, setMainDataByKey]
  )
  const loadGroup = useCallback(
    async (selectedGroupId) => {
      setIsLoading(true)
      let loadedGroup = await apiClient.getJson(
        APIEndpoints.assetGroupAdmin(selectedGroupId).group
      )
      initConfiguration(loadedGroup.assetType.id)
      setIsLoading(false)
    },
    [initConfiguration]
  )
  const handleChangeSelection = useCallback(() => {
    if (!!isGrouped) {
      if (!isEmptyGuid(selectedGroupId)) {
        loadGroup(selectedGroupId)
      } else {
        initConfiguration(emptyGuid)
      }
      setMainDataByKey('group.id', selectedGroupId)
      setFormErrors({})
    } else {
      initConfiguration(selectedTypeId)
      setMainDataByKey('group.id', emptyGuid)
      setFormErrors({})
    }
  }, [
    isGrouped,
    selectedGroupId,
    selectedTypeId,
    setMainDataByKey,
    setFormErrors,
    initConfiguration,
    loadGroup,
  ])
  useEffect(() => {
    handleChangeSelection()
  }, [isGrouped, handleChangeSelection])
  useEffect(() => {
    handleChangeSelection()
  }, [selectedTypeId, handleChangeSelection])
  useEffect(() => {
    handleChangeSelection()
  }, [selectedGroupId, handleChangeSelection])

  const renderIsNotGroupedRadio = () => {
    return (
      <Form.Check
        inline
        type='radio'
        id='isNotGroupedRadio'
        name='isNotGroupedRadio'
        checked={!isGrouped}
        onChange={(e) => setIsGrouped(!e.target.checked)}
      />
    )
  }
  const renderIsGroupedRadio = () => {
    return (
      <Form.Check
        inline
        type='radio'
        id='isGroupedRadio'
        name='isGroupedRadio'
        checked={isGrouped}
        onChange={(e) => setIsGrouped(e.target.checked)}
      />
    )
  }
  return (
    <Form>
      <h2 className='h1'>{t('assetFormMainData:assetForm')}</h2>
      <StyledWrapper>
        <Row>
          <Col sm='6'>
            <Form.Group className='mb-3'>
              <Form.Label className='d-block ps-1'>
                {renderIsNotGroupedRadio()}
                {t('assetFormMainData:type')}
              </Form.Label>
              <ArraySelect
                value={selectedTypeId}
                options={filteredAssetTypes}
                noAutoSelect
                id='assetType'
                onChange={(e) => setSelectedTypeId(e.target.value)}
                disabled={isGrouped}
                returnEvent
              />
            </Form.Group>
          </Col>
          <Col sm='6'>
            <Form.Group className='mb-3'>
              <Form.Label className='d-block ps-1'>
                {renderIsGroupedRadio()}
                {t('assetFormMainData:group')}
              </Form.Label>
              <AssetGroupSelect
                id='assetGroup'
                value={selectedGroupId}
                onChange={(group) => setSelectedGroupId(group)}
                disabled={!isGrouped}
                withSearch
              />
            </Form.Group>
          </Col>
        </Row>
      </StyledWrapper>
      {!!fieldConfiguration && !isLoading && (
        <MainData
          mainData={mainData}
          setMainDataByKey={setMainDataByKey}
          formErrors={formErrors}
          resetErrorByFormKey={resetErrorByFormKey}
          configuration={fieldConfiguration}
        />
      )}
      {isLoading || loadingActivities ? (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner animation='border' variant='primary' />
        </div>
      ) : !!fieldConfiguration &&
        !isLoading &&
        !loadingActivities &&
        selectableAvtivities &&
        selectableAvtivities.length > 0 ? (
        <JobFunctions
          selectableAvtivities={selectableAvtivities}
          activities={activities}
          setActivitiesByKey={setActivitiesByKey}
          formErrors={formErrors}
          resetErrorByFormKey={resetErrorByFormKey}
        />
      ) : null}
      <Alert variant='info'>{t('documents:addDocumentsAfterSaving')}</Alert>
    </Form>
  )
}

export default AddContentArea
